import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import {
  getAction,
  getAutomation,
  getAutomationApp,
  getTrigger,
  postAutomationNodes,
  postAutomationLink,
  createAutomations,
  updateAutomation,
  triggerAutomationRun,
  deactivateNode,
  deactivateLink,
  updateAutomationNode,
  updateAutomationLink,
} from '../../Redux/ActionCreators/automationAction';
import { fetchJobById } from '../../Redux/ActionCreators/jobsAction';
import { useAutomationContext } from './AutomationContext';

// ─── Meta helpers ─────────────────────────────────────────────────────────────

const OP_SYM = { gte: '≥', lte: '≤', eq: '=', gt: '>', lt: '<', ne: '≠' };

function nodeMeta(name, type) {
  const s = (name || '').toLowerCase();
  const sz = 18;
  const p = '#7c3aed'; // purple for triggers
  const g = '#059669'; // green for actions

  // ── Icon factories (stroke color injected) ─────────────────────────────────
  const videoIcon  = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>;
  const phoneIcon  = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.71-.71a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
  // Assessment: checkmark-in-box  (matches IconAssessment)
  const assessIcon = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
  // Pre-screening: clipboard with lines  (matches IconPreScreening)
  const clipIcon   = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" /></svg>;
  // Person/user — for "candidate created" and generic triggers
  const personIcon = (k) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={k} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;

  if (type === 'trigger') {
    let icon;
    if      (s.includes('video'))                                                   icon = videoIcon(p);
    else if (s.includes('voice') || s.includes('telephon') || s.includes('phone')) icon = phoneIcon(p);
    else if (s.includes('assessment'))                                              icon = assessIcon(p);
    else if (s.includes('screen') || s.includes('pre screen'))                     icon = clipIcon(p);
    else                                                                            icon = personIcon(p); // candidate created / other
    return { icon, color: p, bg: '#ede9fe' };
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  if (s.includes('reject')) return {
    icon: <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>,
    color: '#dc2626', bg: '#fee2e2', accent: 'linear-gradient(90deg,#dc2626,#f87171)',
  };
  if (s.includes('shortlist') || s.includes('hire') || s.includes('hired')) return {
    icon: <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
    color: '#d97706', bg: '#fef3c7', accent: 'linear-gradient(90deg,#d97706,#fbbf24)',
  };
  if (s.includes('video'))                                                           return { icon: videoIcon(g),  color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('telephon') || s.includes('voice') || s.includes('phone'))         return { icon: phoneIcon(g),  color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('assessment'))                                                      return { icon: assessIcon(g), color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('screen') || s.includes('pre screen'))                             return { icon: clipIcon(g),   color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
  if (s.includes('email'))                                                           return {
    icon: <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={g} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></svg>,
    color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)',
  };
  return { icon: assessIcon(g), color: g, bg: '#d1fae5', accent: 'linear-gradient(90deg,#059669,#34d399)' };
}

// ─── Send-email action helpers ────────────────────────────────────────────────

const EMAIL_ALLOWED_VARS = ['candidate_name', 'job_title', 'company_name'];

function isSendEmailAction(name, action) {
  return action?.action_key === 'send_email' || (name || '').toLowerCase().includes('send email');
}

// Finds {{var}} tokens in the body that aren't in EMAIL_ALLOWED_VARS.
function getInvalidEmailVariables(text) {
  const matches = [...(text || '').matchAll(/\{\{\s*([a-zA-Z0-9_]*)\s*\}\}/g)];
  const invalid = new Set();
  matches.forEach(m => {
    if (!EMAIL_ALLOWED_VARS.includes(m[1])) invalid.add(`{{${m[1]}}}`);
  });
  return [...invalid];
}

// Saved body is wrapped as "<p>...</p>" with line breaks as "<br />"; unwrap it for editing in a plain textarea.
function stripEmailBodyTags(body) {
  const trimmed = (body || '').trim();
  const unwrapped = (trimmed.startsWith('<p>') && trimmed.endsWith('</p>')) ? trimmed.slice(3, -4) : trimmed;
  return unwrapped.replace(/<br\s*\/?>/gi, '\n');
}

// Returns true if the action node has all required config filled in.
function isActionConfigured(node, actionList) {
  const name   = (node.data?.name || '').toLowerCase();
  const config = node.data?.config || {};
  const action = actionList.find(a => a.id === node.data?.referenceId);

  const isVideoCall  = name.includes('video');
  const isSendEmail  = isSendEmailAction(name, action);
  const needsExpiry  = !isVideoCall && !isSendEmail && !!action?.input_schema?.action_configuration?.is_required;

  if (isSendEmail) return !!(config.subject && config.body);

  if (isVideoCall) {
    // Backend may not return video_interview_type even if previously saved;
    // treat as configured if expiry is present (fallback until backend returns the field).
    if (!config.video_interview_type) return !!(config.valid_till || config.no_of_days);
    if (config.video_interview_type === 'ai_interview') {
      return !!(config.valid_till || config.no_of_days);
    }
    return true;
  }
  if (needsExpiry) return !!(config.valid_till || config.no_of_days);
  return true;
}

// Determine if an automation has meaningful saved positions or needs auto-layout.
// Automations created by the old V1 system all have nodes at the same position.
function hasRealPositions(auto) {
  if (!auto.nodes?.length) return false;
  const xs = auto.nodes.map(n => n.position_x);
  const ys = auto.nodes.map(n => n.position_y);
  return (Math.max(...xs) - Math.min(...xs)) > 250 ||
         (Math.max(...ys) - Math.min(...ys)) > 250;
}

// ─── Custom Nodes ─────────────────────────────────────────────────────────────

const TriggerNode = ({ id, data, selected }) => {
  const m = nodeMeta(data.name, 'trigger');
  return (
    <div className={`afn${selected ? ' afn--sel' : ''}`} style={{ borderColor: selected ? '#7c3aed' : undefined }}>
      <div className="afn__accent" style={{ background: 'linear-gradient(90deg,#7c3aed,#a78bfa)' }} />
      <button
        className="afn__del nodrag nopan"
        title="Remove node"
        onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      >
        ×
      </button>
      <Handle type="target" position={Position.Top} id="in" className="afn__handle" style={{ background: '#7c3aed', borderColor: '#7c3aed' }} />
      <div className="afn__body">
        <div className="afn__icon" style={{ background: m.bg }}>{m.icon}</div>
        <div className="afn__meta">
          <div className="afn__label" style={{ color: '#7c3aed' }}>TRIGGER</div>
          <div className="afn__title">{data.name || 'Trigger'}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="out" className="afn__handle" style={{ background: '#7c3aed', borderColor: '#7c3aed' }} />
    </div>
  );
};

const ActionNode = ({ id, data, selected }) => {
  const m = nodeMeta(data.name, 'action');
  const hasConfig = data.config && Object.keys(data.config).length > 0;
  const borderColor = data.hasError ? '#dc2626' : selected ? m.color : undefined;
  return (
    <div
      className={`afn afn--clickable${selected ? ' afn--sel' : ''}${data.hasError ? ' afn--error' : ''}`}
      style={{ borderColor }}
      onClick={() => data.onConfigure?.(id)}
    >
      <div className="afn__accent" style={{ background: m.accent || 'linear-gradient(90deg,#059669,#34d399)' }} />
      <button
        className="afn__del nodrag nopan"
        title="Remove node"
        onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      >
        ×
      </button>
      <Handle type="target" position={Position.Top} id="in" className="afn__handle" style={{ background: m.color, borderColor: m.color }} />
      <div className="afn__body">
        <div className="afn__icon" style={{ background: m.bg }}>{m.icon}</div>
        <div className="afn__meta">
          <div className="afn__label" style={{ color: m.color }}>ACTION</div>
          <div className="afn__title">{data.name || 'Action'}</div>
          {data.hasError && (
            <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>
              ⚠ Config required
            </div>
          )}
          {!data.hasError && hasConfig && (
            <div style={{ fontSize: 10, color: m.color, fontWeight: 600, marginTop: 2 }}>
              ✓ Configured
            </div>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="out" className="afn__handle" style={{ background: m.color, borderColor: m.color }} />
    </div>
  );
};

// ─── Custom Edge ──────────────────────────────────────────────────────────────

const ConditionEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
  const [path, lx, ly] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const isStage = data?.kind === 'stage';
  const cond = data?.condition;

  let label = 'Always';
  let cls = 'neutral';
  if (isStage) {
    label = 'Waits for event'; cls = 'stage';
  } else if (cond?.op && cond.value != null) {
    label = `${OP_SYM[cond.op] || cond.op} ${cond.value}%`;
    cls = (cond.op === 'gte' || cond.op === 'gt') ? 'pass' : (cond.op === 'lte' || cond.op === 'lt') ? 'fail' : 'neutral';
  }
  const stroke = isStage ? '#a78bfa' : cls === 'pass' ? '#059669' : cls === 'fail' ? '#dc2626' : '#94a3b8';

  return (
    <>
      <BaseEdge path={path} style={{ stroke, strokeWidth: 2.5, strokeDasharray: isStage ? '7 5' : undefined }} />
      <EdgeLabelRenderer>
        <div
          style={{ position: 'absolute', transform: `translate(-50%,-50%) translate(${lx}px,${ly}px)`, pointerEvents: 'all' }}
          className="nodrag nopan"
        >
          <button
            className={`afe-badge afe-badge--${cls}`}
            onClick={() => !isStage && data?.onOpen?.(id)}
          >
            {label}
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

// ─── Action Config Modal ──────────────────────────────────────────────────────

const ActionConfigModal = ({ nodeId, nodes, actionList, onSave, onClose, onError }) => {
  const node    = nodes.find(n => n.id === nodeId);
  const action  = actionList.find(a => a.id === node?.data?.referenceId);
  const existing = node?.data?.config || {};

  const nameLower   = (node?.data?.name || '').toLowerCase();
  const isVideoCall = nameLower.includes('video');
  const isSendEmail = isSendEmailAction(nameLower, action);
  const needsExpiry = !isVideoCall && !isSendEmail && !!action?.input_schema?.action_configuration?.is_required;

  const [videoType,  setVideoType]  = useState(existing.video_interview_type || '');
  const [expireType, setExpireType] = useState(existing.valid_till ? 'date' : existing.no_of_days ? 'days' : 'date');
  const [expireDate, setExpireDate] = useState(existing.valid_till ? new Date(existing.valid_till) : null);
  const [expireDays, setExpireDays] = useState(existing.no_of_days ? String(existing.no_of_days) : '');
  const [subject,    setSubject]    = useState(existing.subject || '');
  const [body,       setBody]       = useState(stripEmailBodyTags(existing.body));

  const showExpiry = isVideoCall ? videoType === 'ai_interview' : needsExpiry;

  const handleSave = () => {
    if (isSendEmail) {
      const trimmedSubject = subject.trim();
      const trimmedBody    = body.trim();
      if (!trimmedSubject || !trimmedBody) {
        onError?.('Subject and body are required');
        return;
      }
      const invalidVars = getInvalidEmailVariables(trimmedBody);
      if (invalidVars.length > 0) {
        onError?.(`Only {{candidate_name}}, {{job_title}}, {{company_name}} are allowed as variables. Remove: ${invalidVars.join(', ')}`);
        return;
      }
      const htmlBody = trimmedBody.replace(/\n/g, '<br />');
      onSave(nodeId, { to: 'candidate', subject: trimmedSubject, body: `<p>${htmlBody}</p>` });
      return;
    }

    let config = {};
    if (isVideoCall) {
      config = { video_interview_type: videoType };
      if (videoType === 'ai_interview') {
        if (expireType === 'date' && expireDate) {
          config = { ...config, expire_at: expireDate.toISOString(), valid_till: expireDate.toISOString(), enable_date: true };
        } else if (expireType === 'days' && expireDays) {
          config = { ...config, enable_days: true, no_of_days: Number(expireDays) };
        }
      }
    } else {
      if (expireType === 'date' && expireDate) {
        config = { expire_at: expireDate.toISOString(), valid_till: expireDate.toISOString(), enable_date: true };
      } else if (expireType === 'days' && expireDays) {
        config = { enable_days: true, no_of_days: Number(expireDays) };
      }
    }
    onSave(nodeId, config);
  };

  return (
    <div className="af-overlay" onClick={onClose}>
      <div className={`af-modal${isSendEmail ? ' af-modal--wide' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="af-modal__head">
          <h3>Configure: {node?.data?.name}</h3>
          <button className="af-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="af-modal__body">
          {isVideoCall && (
            <div className="af-field">
              <label>Interview Type *</label>
              <select value={videoType} onChange={e => { setVideoType(e.target.value); setExpireDate(null); setExpireDays(''); }}>
                <option value="">Select Interview Type</option>
                <option value="hybrid">AI Assisted Interview</option>
                <option value="ai_interview">AI Lead Interview</option>
              </select>
            </div>
          )}

          {isSendEmail && (
            <>
              <div className="af-field">
                <label>Subject *</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                />
              </div>
              <div className="af-field">
                <div className="af-label-row">
                  <label style={{ marginBottom: 0 }}>Body *</label>
                  <span className="af-info-tip" tabIndex={0}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span className="af-info-tip__bubble">
                      Allowed variables: {'{{candidate_name}}'}, {'{{job_title}}'}, {'{{company_name}}'}
                    </span>
                  </span>
                </div>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Enter email body... e.g. Hi {{candidate_name}}, ..."
                  rows={6}
                />
              </div>
            </>
          )}

          {showExpiry && (
            <div className="af-field">
              <label>Expiration</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <div className="expire-toggle-container">
                  <button
                    className={`toggle-btn ${expireType === 'date' ? 'active' : ''}`}
                    onClick={() => { setExpireType('date'); setExpireDays(''); }}
                  >
                    Specific Date
                  </button>
                  <button
                    className={`toggle-btn ${expireType === 'days' ? 'active' : ''}`}
                    onClick={() => { setExpireType('days'); setExpireDate(null); }}
                  >
                    Days After Action
                  </button>
                </div>
                <div className="expire-input-area">
                  {expireType === 'date' && (
                    <DatePicker
                      selected={expireDate}
                      onChange={date => {
                        if (date) { const d = new Date(date); d.setHours(23, 59, 59, 999); setExpireDate(d); }
                        else setExpireDate(null);
                      }}
                      placeholderText="Select exact date..."
                      className="form-control"
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy"
                    />
                  )}
                  {expireType === 'days' && (
                    <select className="form-control" value={expireDays} onChange={e => setExpireDays(e.target.value)}>
                      <option value="">Select duration...</option>
                      {[1, 2, 3, 4, 5, 6, 7].map(day => (
                        <option key={day} value={day}>Expire {day} day{day > 1 ? 's' : ''} later</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isVideoCall && !isSendEmail && !needsExpiry && (
            <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>No additional configuration required for this action.</p>
          )}
        </div>
        <div className="af-modal__foot">
          <button className="af-btn af-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="af-btn af-btn--primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { triggerNode: TriggerNode, actionNode: ActionNode };
const edgeTypes = { conditionEdge: ConditionEdge };

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const Sidebar = ({ triggerList, actionList, tab, onTabChange, onAddCenter, usedTriggerIds, usedActionIds }) => {
  const items = tab === 'triggers' ? triggerList : actionList;
  const type  = tab === 'triggers' ? 'trigger' : 'action';

  const onDragStart = (e, item, disabled) => {
    if (disabled) { e.preventDefault(); return; }
    e.dataTransfer.setData('application/rf/type', type);
    e.dataTransfer.setData('application/rf/id', item.id);
    e.dataTransfer.setData('application/rf/name', item.name);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <aside className="af-sidebar">
      <div className="af-sidebar__tabs">
        <button className={`af-sidebar__tab${tab === 'triggers' ? ' active' : ''}`} onClick={() => onTabChange('triggers')}>Triggers</button>
        <button className={`af-sidebar__tab${tab === 'actions' ? ' active' : ''}`} onClick={() => onTabChange('actions')}>Actions</button>
      </div>
      <p className="af-sidebar__hint">Drag a block onto the canvas or click to add it.</p>
      <div className="af-sidebar__list">
        {items.map(item => {
          const m = nodeMeta(item.name, type);
          const nameLower = item.name.toLowerCase();
          // These outcome actions are valid on multiple branches so never disable them
          const isMultiAllowed = type === 'action' && (
            nameLower.includes('shortlist') ||
            nameLower.includes('hired') ||
            nameLower.includes('hire') ||
            nameLower.includes('reject') ||
            nameLower.includes('on hold') ||
            nameLower.includes('hold') ||
            nameLower.includes('email')
          );
          const isDisabled = isMultiAllowed ? false : (
            type === 'trigger'
              ? usedTriggerIds.includes(item.id)
              : usedActionIds.includes(item.id)
          );
          return (
            <div
              key={item.id}
              className={`af-lib-item${isDisabled ? ' af-lib-item--disabled' : ''}`}
              draggable={!isDisabled}
              onDragStart={e => onDragStart(e, item, isDisabled)}
              onClick={() => !isDisabled && onAddCenter(type, item)}
              title={isDisabled ? 'Already added to the canvas' : undefined}
            >
              <div className="af-lib-icon" style={{ background: m.bg }}>{m.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="af-lib-name">{item.name}</div>
                <div className="af-lib-sub">{type === 'trigger' ? 'When this happens' : 'Do this'}</div>
              </div>
              {isDisabled && <span className="af-lib-added">Added</span>}
            </div>
          );
        })}
      </div>
      <div className="af-sidebar__rules">
        <strong>Connecting</strong>
        Trigger → action: sets a score rule<br />
        Action → trigger: waits for event
      </div>
    </aside>
  );
};

// ─── Condition Modal ──────────────────────────────────────────────────────────

const ConditionModal = ({ edgeId, edges, onSave, onClose, onDelete }) => {
  const edge = edges.find(e => e.id === edgeId);
  const [op, setOp] = useState(edge?.data?.condition?.op || '');
  const [val, setVal] = useState(edge?.data?.condition?.value ?? '');

  let preview = 'Always proceed to this action';
  if (op && val !== '') preview = `When score is ${OP_SYM[op] || op} ${val}%, run this action`;
  else if (op) preview = 'Enter a score value';

  return (
    <div className="af-overlay" onClick={onClose}>
      <div className="af-modal" onClick={e => e.stopPropagation()}>
        <div className="af-modal__head">
          <h3>Score Rule</h3>
          <button className="af-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="af-modal__body">
          <div className="af-presets">
            <button className="af-preset af-preset--pass" onClick={() => { setOp('gte'); setVal(40); }}>
              <strong>Pass</strong><span>≥ 40%</span>
            </button>
            <button className="af-preset af-preset--fail" onClick={() => { setOp('lte'); setVal(20); }}>
              <strong>Fail</strong><span>≤ 20%</span>
            </button>
            <button className="af-preset" onClick={() => { setOp(''); setVal(''); }}>
              <strong>Always</strong><span>No rule</span>
            </button>
          </div>
          <div className="af-field-row">
            <div className="af-field">
              <label>Condition</label>
              <select value={op} onChange={e => setOp(e.target.value)}>
                <option value="gte">≥ at least</option>
                <option value="lte">≤ at most</option>
                <option value="gt">&gt; greater than</option>
                <option value="lt">&lt; less than</option>
                <option value="eq">= equals</option>
                <option value="ne">≠ not equal</option>
              </select>
            </div>
            <div className="af-field">
              <label>Score %</label>
              <input
                type="number" min="0" max="100"
                value={val}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '') { setVal(''); return; }
                  let n = Math.floor(Number(v));
                  if (isNaN(n) || n < 0) n = 0;
                  if (n > 100) n = 100;
                  setVal(n);
                }}
              />
            </div>
          </div>
          <div className="af-preview">{preview}</div>
        </div>
        <div className="af-modal__foot">
          <button
            className="af-btn af-btn--ghost"
            style={{ color: '#dc2626', borderColor: '#dc2626', marginRight: 'auto' }}
            onClick={() => onDelete?.(edgeId)}
          >
            Remove link
          </button>
          <button className="af-btn af-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="af-btn af-btn--primary" onClick={() => onSave(edgeId, op, val)}>Save</button>
        </div>
      </div>
    </div>
  );
};

// ─── Inner flow component (needs useReactFlow) ────────────────────────────────

const GRID_COLS = 3;
const CELL_W    = 500;
const CELL_H    = 180;
const GRID_X0   = 50;
const GRID_Y0   = 50;

// Convert all automations from the API into a flat array of RF nodes and edges.
function buildFlowGraph(automationDetails, triggerList, actionList, handleDeleteNode, setCondModalEdgeId, handleConfigOpen, handleDeleteEdge) {
  const allNodes = [];
  const allEdges = [];

  const simple  = automationDetails.filter(a => !hasRealPositions(a));
  const complex = automationDetails.filter(a =>  hasRealPositions(a));

  const simpleRows  = Math.ceil(simple.length / GRID_COLS);
  const complexBaseY = GRID_Y0 + simpleRows * CELL_H + 80;

  // ── Simple automations: grid layout (trigger left, action right in each cell) ──
  simple.forEach((auto, idx) => {
    const col   = idx % GRID_COLS;
    const row   = Math.floor(idx / GRID_COLS);
    const baseX = GRID_X0 + col * CELL_W;
    const baseY = GRID_Y0 + row * CELL_H;
    const nodeMap = {};

    (auto.nodes || []).forEach((node) => {
      const isTrig = node.type === 'trigger';
      const meta   = (isTrig ? triggerList : actionList).find(t => t.id === node.reference_id);
      const rfId   = `loaded-${node.id}`;
      nodeMap[node.id] = rfId;

      allNodes.push({
        id: rfId,
        type: isTrig ? 'triggerNode' : 'actionNode',
        position: { x: baseX + (isTrig ? 0 : 260), y: baseY },
        data: {
          name: meta?.name || (isTrig ? 'Trigger' : 'Action'),
          apiId: node.id,
          automationId: auto.id,
          referenceId: node.reference_id,
          config: node.config || {},
          onDelete: handleDeleteNode,
          onConfigure: isTrig ? undefined : handleConfigOpen,
        },
      });
    });

    (auto.links || []).forEach((link) => {
      const src = nodeMap[link.from_node_id];
      const tgt = nodeMap[link.to_node_id];
      if (!src || !tgt) return;
      const cond = link.link_condition;
      const isStageLink = !cond || cond?.edge_kind === 'waits_for_event';
      allEdges.push({
        id: `loaded-link-${link.id}`,
        source: src, target: tgt,
        type: 'conditionEdge',
        data: {
          condition: isStageLink ? null : cond,
          kind: isStageLink ? 'stage' : 'rule',
          onOpen: isStageLink ? undefined : setCondModalEdgeId,
          onDelete: handleDeleteEdge,
          apiId: link.id,
          automationId: auto.id,
        },
      });
    });
  });

  // ── Complex automations: use saved positions, normalized to (GRID_X0, complexBaseY) ──
  let complexXOffset = GRID_X0;

  complex.forEach((auto) => {
    const nodeMap = {};
    const xs = (auto.nodes || []).map(n => n.position_x);
    const ys = (auto.nodes || []).map(n => n.position_y);
    const minX = xs.length ? Math.min(...xs) : 0;
    const minY = ys.length ? Math.min(...ys) : 0;
    const maxX = xs.length ? Math.max(...xs) : 0;

    (auto.nodes || []).forEach((node) => {
      const isTrig = node.type === 'trigger';
      const meta   = (isTrig ? triggerList : actionList).find(t => t.id === node.reference_id);
      const rfId   = `loaded-${node.id}`;
      nodeMap[node.id] = rfId;

      allNodes.push({
        id: rfId,
        type: isTrig ? 'triggerNode' : 'actionNode',
        position: {
          x: complexXOffset + (node.position_x - minX),
          y: complexBaseY  + (node.position_y - minY),
        },
        data: {
          name: meta?.name || (isTrig ? 'Trigger' : 'Action'),
          apiId: node.id,
          automationId: auto.id,
          referenceId: node.reference_id,
          config: node.config || {},
          onDelete: handleDeleteNode,
          onConfigure: isTrig ? undefined : handleConfigOpen,
        },
      });
    });

    (auto.links || []).forEach((link) => {
      const src = nodeMap[link.from_node_id];
      const tgt = nodeMap[link.to_node_id];
      if (!src || !tgt) return;
      const cond = link.link_condition;
      const isStageLink = !cond || cond?.edge_kind === 'waits_for_event';
      allEdges.push({
        id: `loaded-link-${link.id}`,
        source: src, target: tgt,
        type: 'conditionEdge',
        data: {
          condition: isStageLink ? null : cond,
          kind: isStageLink ? 'stage' : 'rule',
          onOpen: isStageLink ? undefined : setCondModalEdgeId,
          onDelete: handleDeleteEdge,
          apiId: link.id,
          automationId: auto.id,
        },
      });
    });

    // Next complex automation starts to the right of this one + gap
    complexXOffset += (maxX - minX) + 300;
  });

  return { allNodes, allEdges };
}

// ─────────────────────────────────────────────────────────────────────────────

const AutomationFlowInner = ({ jobId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fitView, screenToFlowPosition, zoomIn, zoomOut } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [automationIds, setAutomationIds] = useState([]);   // all loaded automation IDs
  const [automationActive, setAutomationActive] = useState(false);
  const [condModalEdgeId, setCondModalEdgeId] = useState(null);
  const [configModalNodeId, setConfigModalNodeId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('triggers');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });

  // Snapshot of the last persisted state — Reset restores to this
  const savedStateRef = useRef({ nodes: [], edges: [] });

  const ctx = useAutomationContext();
  // ctx is a new object on every JobTabsV2 render. Storing setControls in a ref
  // breaks the infinite loop: effect can call setControls without ctx being a dep.
  const setCtrlRef = useRef(null);
  setCtrlRef.current = ctx?.setControls ?? null;

  const tenantId = localStorage.getItem('tenentId');

  const automationApp     = useSelector(s => s.automation?.automationApp || null);
  const automationDetails = useSelector(s => s.automation?.automationDetails || null);
  const triggerList       = useSelector(s => s.automation?.triggerDetails || []);
  const actionList        = useSelector(s => s.automation?.actionDetails || []);
  const { selectedTenant } = useSelector(s => s.users);

  const automationEnabled =
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags?.automation_workflows === true ||
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags?.automation === true;

  // ── Delete a node and all its connected edges ──
  const handleDeleteNode = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const handleDeleteEdge = useCallback((edgeId) => {
    setEdges(eds => eds.filter(e => e.id !== edgeId));
  }, [setEdges]);

  // ── Open / save action config modal ──
  const handleConfigOpen = useCallback((nodeId) => {
    setConfigModalNodeId(nodeId);
  }, []);

  const handleConfigSave = useCallback((nodeId, config) => {
    setNodes(nds => nds.map(n => n.id !== nodeId ? n : { ...n, data: { ...n.data, config, hasError: false } }));
    setConfigModalNodeId(null);
  }, [setNodes]);

  // ── Trigger IDs already on the canvas (disables them in the sidebar) ──
  const usedTriggerIds = useMemo(
    () => nodes.filter(n => n.type === 'triggerNode').map(n => n.data.referenceId).filter(Boolean),
    [nodes]
  );

  const usedActionIds = useMemo(
    () => nodes.filter(n => n.type === 'actionNode').map(n => n.data.referenceId).filter(Boolean),
    [nodes]
  );

  // ── Detect unsaved changes ──
  // Covers: new nodes/edges, removed nodes/edges, moved nodes, edited link conditions.
  // savedStateRef is a ref so not in deps, but it only mutates right before
  // setNodes/setEdges calls which retrigger this memo anyway.
  const hasChanges = useMemo(() => {
    const { nodes: sn, edges: se } = savedStateRef.current;

    // New items (no apiId yet)
    if (nodes.some(n => !n.data?.apiId)) return true;
    if (edges.some(e => !e.data?.apiId)) return true;

    // Removed items (in snapshot but gone from canvas)
    if (sn.some(s => s.data?.apiId && !nodes.find(n => n.id === s.id))) return true;
    if (se.some(s => s.data?.apiId && !edges.find(e => e.id === s.id))) return true;

    // Position or config changed on an existing node
    for (const node of nodes) {
      if (!node.data?.apiId) continue;
      const saved = sn.find(s => s.id === node.id);
      if (saved &&
          (Math.round(node.position.x) !== Math.round(saved.position.x) ||
           Math.round(node.position.y) !== Math.round(saved.position.y))) return true;
      if (saved &&
          JSON.stringify(node.data?.config ?? {}) !== JSON.stringify(saved.data?.config ?? {})) return true;
    }

    // Condition changed on an existing edge
    for (const edge of edges) {
      if (!edge.data?.apiId) continue;
      const saved = se.find(s => s.id === edge.id);
      if (saved &&
          JSON.stringify(edge.data?.condition ?? null) !== JSON.stringify(saved.data?.condition ?? null))
        return true;
    }

    return false;
  }, [nodes, edges]);

  // ── Initial data fetch ──
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await dispatch(fetchJobById(jobId));
      await dispatch(getAutomationApp({ page: 1, page_size: 20 }));
      await dispatch(getTrigger());
      await dispatch(getAction());
      setLoading(false);
    };
    init();
  }, [dispatch, jobId]);

  useEffect(() => {
    if (!automationApp?.length) return;
    dispatch(getAutomation({ job_id: jobId, tenant_id: tenantId, app_id: automationApp[0].id }));
  }, [automationApp, jobId, tenantId, dispatch]);

  // ── Convert ALL automations → React Flow graph and snapshot it ──
  useEffect(() => {
    if (!triggerList.length || !actionList.length) return;
    if (automationDetails == null) return; // not fetched yet for this job

    // Job has no automations — reset canvas instead of leaving a previous job's state on screen
    if (!automationDetails.length) {
      setAutomationIds([]);
      setAutomationActive(false);
      savedStateRef.current = { nodes: [], edges: [] };
      setNodes([]);
      setEdges([]);
      return;
    }

    // Track all IDs; active = any automation is active
    setAutomationIds(automationDetails.map(a => a.id));
    setAutomationActive(automationDetails.some(a => a.is_active));

    const { allNodes, allEdges } = buildFlowGraph(
      automationDetails,
      triggerList,
      actionList,
      handleDeleteNode,
      setCondModalEdgeId,
      handleConfigOpen,
      handleDeleteEdge,
    );

    savedStateRef.current = { nodes: allNodes, edges: allEdges };
    setNodes(allNodes);
    setEdges(allEdges);
    setTimeout(() => fitView({ padding: 0.1 }), 150);
  }, [automationDetails, triggerList, actionList, handleDeleteNode, handleDeleteEdge, handleConfigOpen, setNodes, setEdges, fitView]);

  // ── Connect nodes ──
  const onConnect = useCallback((params) => {
    const src = nodes.find(n => n.id === params.source);
    const tgt = nodes.find(n => n.id === params.target);
    if (!src || !tgt) return;

    const isRule  = src.type === 'triggerNode' && tgt.type === 'actionNode';
    const isStage = src.type === 'actionNode'  && tgt.type === 'triggerNode';
    if (!isRule && !isStage) {
      setSnack({ open: true, message: 'Connect trigger→action or action→trigger only', severity: 'warning' });
      return;
    }

    const newId = `edge-${Date.now()}`;
    setEdges(eds => addEdge({
      ...params, id: newId, type: 'conditionEdge',
      data: { kind: isRule ? 'rule' : 'stage', condition: null, onOpen: isRule ? setCondModalEdgeId : undefined, onDelete: handleDeleteEdge },
    }, eds));

    if (isRule) setTimeout(() => setCondModalEdgeId(newId), 50);
  }, [nodes, setEdges, handleDeleteEdge]);

  // ── Drag & drop from sidebar ──
  const onDragOver = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const onDrop = useCallback(e => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/rf/type');
    const id   = e.dataTransfer.getData('application/rf/id');
    const name = e.dataTransfer.getData('application/rf/name');
    if (!type || !id) return;
    const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    setNodes(nds => [...nds, {
      id: `new-${Date.now()}`,
      type: type === 'trigger' ? 'triggerNode' : 'actionNode',
      position: pos,
      data: { name, referenceId: id, config: {}, onDelete: handleDeleteNode, onConfigure: type === 'action' ? handleConfigOpen : undefined },
    }]);
  }, [screenToFlowPosition, setNodes, handleDeleteNode, handleConfigOpen]);

  // ── Add node at center via sidebar click ──
  const handleAddCenter = useCallback((type, item) => {
    setNodes(nds => {
      const offset = nds.length * 30;
      return [...nds, {
        id: `new-${Date.now()}`,
        type: type === 'trigger' ? 'triggerNode' : 'actionNode',
        position: { x: 200 + offset, y: 200 + offset },
        data: { name: item.name, referenceId: item.id, config: {}, onDelete: handleDeleteNode, onConfigure: type === 'action' ? handleConfigOpen : undefined },
      }];
    });
  }, [setNodes, handleDeleteNode, handleConfigOpen]);

  // ── Save condition on edge ──
  const handleCondSave = useCallback((edgeId, op, val) => {
    setEdges(eds => eds.map(e => e.id !== edgeId ? e : {
      ...e, data: { ...e.data, condition: op ? { op, value: Number(val) } : null },
    }));
    setCondModalEdgeId(null);
  }, [setEdges]);

  // ── Save automation to API ──
  const handleSave = useCallback(async () => {
    const { nodes: savedNodes, edges: savedEdges } = savedStateRef.current;

    // Nodes/edges that existed in the last API snapshot but are no longer on the canvas
    const removedNodes = savedNodes.filter(
      sn => sn.data?.apiId && !nodes.some(n => n.id === sn.id)
    );
    const removedEdges = savedEdges.filter(
      se => se.data?.apiId && !edges.some(e => e.id === se.id)
    );

    // Nodes/edges that are brand-new (no apiId yet)
    const newNodes = nodes.filter(n => !n.data?.apiId);
    const newEdges = edges.filter(e => !e.data?.apiId);

    // Existing nodes whose position moved
    const movedNodes = nodes.filter(n => {
      if (!n.data?.apiId) return false;
      const saved = savedNodes.find(s => s.id === n.id);
      return saved &&
        (Math.round(n.position.x) !== Math.round(saved.position.x) ||
         Math.round(n.position.y) !== Math.round(saved.position.y));
    });

    // Existing nodes whose config changed
    const configChangedNodes = nodes.filter(n => {
      if (!n.data?.apiId) return false;
      const saved = savedNodes.find(s => s.id === n.id);
      return saved &&
        JSON.stringify(n.data?.config ?? {}) !== JSON.stringify(saved.data?.config ?? {});
    });

    // Existing edges whose condition changed
    const changedEdges = edges.filter(e => {
      if (!e.data?.apiId) return false;
      const saved = savedEdges.find(s => s.id === e.id);
      return saved &&
        JSON.stringify(e.data?.condition ?? null) !== JSON.stringify(saved.data?.condition ?? null);
    });

    const ruleEdges = newEdges.filter(e => e.data?.kind === 'rule');
    const anythingChanged =
      removedNodes.length > 0 || removedEdges.length > 0 ||
      newNodes.length > 0 || newEdges.length > 0 || movedNodes.length > 0 || changedEdges.length > 0 ||
      configChangedNodes.length > 0;

    if (!anythingChanged) return setSnack({ open: true, message: 'No changes to save', severity: 'info' });
    if (newNodes.length > 0 && !ruleEdges.length)
      return setSnack({ open: true, message: 'Connect at least one trigger to an action', severity: 'error' });

    // ── Validate action node config (expiry / interview type) ──
    const newActionNodes = newNodes.filter(n => n.type === 'actionNode');
    const unconfigured  = newActionNodes.filter(n => !isActionConfigured(n, actionList));
    if (unconfigured.length > 0) {
      setNodes(nds => nds.map(n =>
        unconfigured.some(u => u.id === n.id)
          ? { ...n, data: { ...n.data, hasError: true } }
          : n
      ));
      return setSnack({ open: true, message: 'Configure all action nodes before saving', severity: 'error' });
    }

    try {
      setSaving(true);

      // ── Step 1: deactivate removed links first (before their nodes) ──
      await Promise.all(
        removedEdges.map(e => dispatch(deactivateLink(e.data.automationId, e.data.apiId)))
      );

      // ── Step 2: deactivate removed nodes ──
      await Promise.all(
        removedNodes.map(n => dispatch(deactivateNode(n.data.automationId, n.data.apiId)))
      );

      // ── Step 2a: update positions of moved existing nodes ──
      await Promise.all(
        movedNodes.map(n => dispatch(updateAutomationNode(n.data.automationId, n.data.apiId, {
          position_x: Math.round(n.position.x),
          position_y: Math.round(n.position.y),
        })))
      );

      // ── Step 2b-config: update config of existing nodes whose config changed ──
      await Promise.all(
        configChangedNodes.map(n => dispatch(updateAutomationNode(n.data.automationId, n.data.apiId, {
          config: n.data.config || {},
        })))
      );

      // ── Step 2b: update conditions of changed existing edges ──
      for (const edge of changedEdges) {
        const srcNode = nodes.find(n => n.id === edge.source);
        const trigger = triggerList.find(t => t.id === srcNode?.data?.referenceId);
        const cond = edge.data?.condition;
        await dispatch(updateAutomationLink(edge.data.automationId, edge.data.apiId, {
          link_condition: cond
            ? { field: trigger?.output_schema?.field, op: cond.op, value: Number(cond.value) }
            : null,
        }));
      }

      // ── Step 3: reuse existing automation or create one, then add new nodes + links ──
      if (newNodes.length > 0 || newEdges.length > 0) {
        let autoId;

        if (automationIds.length > 0) {
          autoId = automationIds[0];
        } else {
          const autoResp = await dispatch(createAutomations({
            name: `automation rule 1`,
            description: '',
            app_id: automationApp?.[0]?.id,
            is_template: false,
            job_id: jobId,
            tenant_id: Number(tenantId),
          }));
          if (!autoResp?.id) throw new Error('Failed to create automation');
          autoId = autoResp.id;
        }

        const nodeIdMap = {};

        for (const rfNode of newNodes) {
          const isTrig = rfNode.type === 'triggerNode';
          if (!rfNode.data.referenceId) continue;
          const resp = await dispatch(postAutomationNodes(autoId, {
            type: isTrig ? 'trigger' : 'action',
            reference_id: rfNode.data.referenceId,
            config: rfNode.data.config || {},
            position_x: Math.round(rfNode.position.x),
            position_y: Math.round(rfNode.position.y),
          }));
          if (resp?.id) nodeIdMap[rfNode.id] = resp.id;
        }

        for (const edge of newEdges) {
          const srcNode = nodes.find(n => n.id === edge.source);
          const tgtNode = nodes.find(n => n.id === edge.target);

          // New nodes use nodeIdMap; existing nodes use their persisted apiId
          const fromApiId = nodeIdMap[edge.source] || srcNode?.data?.apiId;
          const toApiId   = nodeIdMap[edge.target] || tgtNode?.data?.apiId;
          if (!fromApiId || !toApiId) continue;

          // For edges between existing nodes, use their automation; otherwise the new/reused one
          const edgeAutoId = srcNode?.data?.automationId || tgtNode?.data?.automationId || autoId;
          if (!edgeAutoId) continue;

          const isStageEdge = edge.data?.kind === 'stage';
          const triggerRfNode = srcNode?.type === 'triggerNode' ? srcNode : null;
          const trigger  = triggerList.find(t => t.id === triggerRfNode?.data?.referenceId);
          const cond     = edge.data?.condition;

          const linkCondition = isStageEdge
            ? { type: 'always', label: 'Waits for event', edge_kind: 'waits_for_event' }
            : (cond ? { field: trigger?.output_schema?.field, op: cond.op, value: Number(cond.value) } : null);

          await dispatch(postAutomationLink(edgeAutoId, {
            from_node_id: fromApiId,
            to_node_id: toApiId,
            link_condition: linkCondition,
          }));
        }

        if (newNodes.length > 0) {
          await dispatch(triggerAutomationRun(autoId, 'IN_PROGRESS'));
          setAutomationIds(prev => prev.includes(autoId) ? prev : [...prev, autoId]);
          setAutomationActive(true);
        }
      }

      setSnack({ open: true, message: 'Automation saved successfully!', severity: 'success' });

      // ── Step 4: refresh canvas from API (shows deactivated items gone + new items added) ──
      await dispatch(getAutomation({ job_id: jobId, tenant_id: tenantId, app_id: automationApp?.[0]?.id }));
    } catch (err) {
      setSnack({ open: true, message: err.message || 'Failed to save automation', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, dispatch, automationApp, jobId, tenantId, automationIds, triggerList, actionList, setNodes]);

  // ── Toggle all automations on/off (only flips is_active, then refreshes) ──
  const handleToggle = useCallback(async () => {
    if (!automationIds.length) return setSnack({ open: true, message: 'No automations to toggle', severity: 'warning' });
    try {
      const next = !automationActive;
      await Promise.all(
        automationIds.map(id => dispatch(updateAutomation(id, { is_active: next })))
      );
      // Optimistic update for instant UI feedback
      setAutomationActive(next);
      // Refresh from API — automationDetails useEffect will reconcile and rebuild canvas
      await dispatch(getAutomation({ job_id: jobId, tenant_id: tenantId, app_id: automationApp?.[0]?.id }));
    } catch {
      setSnack({ open: true, message: 'Failed to update automation status', severity: 'error' });
    }
  }, [automationIds, automationActive, dispatch, jobId, tenantId, automationApp]);

  // ── Reset to last saved / API state ──
  const handleReset = useCallback(() => {
    const { nodes: savedNodes, edges: savedEdges } = savedStateRef.current;
    setNodes(savedNodes);
    setEdges(savedEdges);
    setTimeout(() => { if (savedNodes.length > 0) fitView({ padding: 0.1 }); }, 100);
  }, [setNodes, setEdges, fitView]);

  const handleFit = useCallback(() => fitView({ padding: 0.1 }), [fitView]);

  // ── Register controls with JobTabsV2 header ──
  // ctx is intentionally NOT in deps — it's a new object every render and
  // including it would cause an infinite setState loop. setCtrlRef.current is
  // updated synchronously each render so it always holds the current setter.
  useEffect(() => {
    setCtrlRef.current?.({
      isActive: automationActive,
      hasAutomation: automationIds.length > 0,
      hasChanges,
      saving,
      onToggle: handleToggle,
      onFit: handleFit,
      onReset: handleReset,
      onSave: handleSave,
    });
    return () => setCtrlRef.current?.(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationActive, automationIds, hasChanges, saving, handleToggle, handleFit, handleReset, handleSave]);

  // ── Upgrade screen ──
  if (!automationEnabled) {
    return (
      <div className="af-upgrade">
        <h3>Feature Not Available</h3>
        <p>This feature is not active with your current plan.<br />Please upgrade to unlock this feature.</p>
        <button
          style={{ background: '#f75891', color: '#fff', border: 'none', borderRadius: 4, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
          onClick={() => navigate('/plan')}
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="af-loading"><div className="spinner-border" /></div>;
  }

  // ── Inactive state: automations exist but are all turned off ──
  if (automationIds.length > 0 && !automationActive) {
    return (
      <>
        <div className="af-inactive">
          <div className="af-inactive__icon">⏸</div>
          <h3 className="af-inactive__title">Automation is not active</h3>
          <p className="af-inactive__text">
            Turn on automation to start processing candidates automatically based on your configured rules.
          </p>
          <div className="af-inactive__toggle-row">
            <span className="af-inactive__label">Automation</span>
            <button
              className="auto-toggle-btn"
              onClick={handleToggle}
              aria-label="Enable automation"
            />
            <span className="auto-toggle-label">Off</span>
          </div>
        </div>
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <div className="af-layout">
      <Sidebar
        triggerList={triggerList}
        actionList={actionList}
        tab={sidebarTab}
        onTabChange={setSidebarTab}
        onAddCenter={handleAddCenter}
        usedTriggerIds={usedTriggerIds}
        usedActionIds={usedActionIds}
      />

      <div className="af-canvas-wrap">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          deleteKeyCode="Delete"
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#cbd5e1" />
        </ReactFlow>
        <div className="af-zoom-controls">
          <button className="af-zoom-btn" onClick={() => zoomIn({ duration: 200 })} title="Zoom in">+</button>
          <button className="af-zoom-btn" onClick={() => zoomOut({ duration: 200 })} title="Zoom out">−</button>
        </div>
        {nodes.length === 0 && (
          <div className="af-canvas-empty">
            <div className="af-canvas-empty__icon">🔧</div>
            <p>Drag triggers and actions from the sidebar to build your flow</p>
          </div>
        )}
      </div>

      {condModalEdgeId && (
        <ConditionModal
          edgeId={condModalEdgeId}
          edges={edges}
          onSave={handleCondSave}
          onClose={() => setCondModalEdgeId(null)}
          onDelete={(edgeId) => { handleDeleteEdge(edgeId); setCondModalEdgeId(null); }}
        />
      )}

      {configModalNodeId && (
        <ActionConfigModal
          nodeId={configModalNodeId}
          nodes={nodes}
          actionList={actionList}
          onSave={handleConfigSave}
          onClose={() => setConfigModalNodeId(null)}
          onError={(message) => setSnack({ open: true, message, severity: 'error' })}
        />
      )}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </div>
  );
};

// ─── Public export (wraps with ReactFlowProvider) ─────────────────────────────

const AutomationV2 = ({ jobId: propJobId }) => {
  const { jobId: routeJobId } = useParams();
  const jobId = propJobId || routeJobId;
  return (
    <ReactFlowProvider>
      <AutomationFlowInner jobId={jobId} />
    </ReactFlowProvider>
  );
};

export default AutomationV2;
